import React, { useState } from 'react';
import { AsyncInputNumber, Price } from '@partsbadger/library';
import { CloseOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';

import { Button, Form, Descriptions, Tooltip, Popover } from 'antd';

interface Props {
    showTitles?: boolean;
    quantity: number;
    unit_price: number;
    total_price: number;
    lead_time: number;
    max_quantity: number;
    handleRemove?: () => void;
    handleSave?: (item: { quantity: number; unit_price: number; total_price: number; lead_time: number }) => void;
    editable: boolean;
    hidePrices?: boolean;
    handleChangeQuantity?: (
        quantity: number
    ) => Promise<{
        quantity: number;
        unit_price: number;
        total_price: number;
        lead_time: number;
    }>;
}

const QuotedQuantity: React.FC<Props> = props => {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [validateStatus, setValidateStatus] = useState<'success' | 'error'>('success');

    const { quantity, unit_price, total_price, handleRemove, editable, handleChangeQuantity } = props;

    const showExtraColumn = !!handleRemove;
    const columnClassName = showExtraColumn
        ? 'd-flex flex-column items-center w-1/5'
        : 'd-flex flex-column items-center w-1/4';

    return (
        <div className="d-flex flex-row w-100">
            <div className="d-flex flex-row justify-between w-100 text-right">
                <div className={columnClassName}>
                    {props.showTitles && <div className="font-size-sm font-weight-bold">Lead Time</div>}
                    <div className={'h-full flex flex-col justify-center'}>
                        {!props.hidePrices ? <>{props.lead_time} days</> : 'Pending'}
                    </div>
                </div>
                <div className={columnClassName}>
                    {!props.hidePrices && <span className="font-size-sm font-weight-bold">Quantity</span>}

                    {editable ? (
                        <div className="ml-auto flex flex-col items-center justify-center">
                            <Form.Item
                                validateStatus={validateStatus}
                                help={
                                    validateStatus === 'error' && `Select a number between 1 and ${props.max_quantity}`
                                }
                                className="m-0"
                            >
                                <AsyncInputNumber
                                    max={props.max_quantity}
                                    defaultValue={quantity}
                                    style={{ width: '70px' }}
                                    size="small"
                                    className="text-sm lg:text-md"
                                    onChange={async (qty: number) => {
                                        setIsLoading(true);

                                        if (qty > props.max_quantity) {
                                            setValidateStatus('error');
                                            return;
                                        }

                                        setValidateStatus('success');

                                        try {
                                            handleChangeQuantity && (await handleChangeQuantity(qty));
                                        } catch (e) {
                                            console.log(e);
                                        }
                                        setIsLoading(false);
                                    }}
                                />
                            </Form.Item>
                        </div>
                    ) : (
                        <>
                            <span className={'h-full flex flex-col justify-center'}>{quantity}</span>
                        </>
                    )}
                </div>

                <div className={columnClassName}>
                    {props.showTitles && <span className="font-size-sm font-weight-bold">Unit Price</span>}
                    <div className={'h-full flex flex-col justify-center'}>
                        {!props.hidePrices ? <Price value={unit_price} /> : 'Pending'}
                    </div>
                </div>
                <div className={columnClassName}>
                    {props.showTitles && <span className="font-size-sm font-weight-bold">Total Price</span>}
                    <div className={'h-full flex flex-col justify-center'}>
                        {!props.hidePrices ? <Price value={total_price} /> : 'Pending'}
                    </div>
                </div>

                {showExtraColumn && (
                    <div className={columnClassName}>
                        {props.showTitles && <span className="font-size-sm font-weight-bold"> &nbsp;</span>}
                        <div className={'h-full flex flex-col justify-center'}>
                            {handleRemove && (
                                <div className="d-flex justify-content-end" style={{ width: '40px' }}>
                                    <Tooltip title="Remove From Quote">
                                        <Button type="link" className="m-0 p-0" onClick={() => handleRemove()}>
                                            <CloseOutlined />
                                        </Button>
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuotedQuantity;
